// src/components/user/controltower/useControlTower.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { socket } from '../../admin/chat/socket';

const BASE_URL = process.env.REACT_APP_API_URL || '';
const POLL_MS = 5000;
const ALERTS_POLL_MS = 60000;

function authConfig(extra = {}) {
  const token = window.localStorage.getItem('token');
  return {
    ...extra,
    headers: {
      ...(extra.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
}

function isActiveAlert(alert) {
  return alert?.status === 'open' || alert?.status === 'acknowledged';
}

function upsertAlert(alerts, incoming) {
  if (!incoming?.id) return alerts;

  if (!isActiveAlert(incoming)) {
    return alerts.filter(alert => alert.id !== incoming.id);
  }

  const exists = alerts.some(alert => alert.id === incoming.id);
  if (exists) {
    return alerts.map(alert => alert.id === incoming.id ? incoming : alert);
  }

  return [incoming, ...alerts];
}

function upsertDriver(drivers, incoming) {
  if (!incoming?.id && !incoming?.user_id && !incoming?.driver_profile_id) return drivers;

  const incomingUserId = incoming.user_id || incoming.id;
  const incomingProfileId = incoming.driver_profile_id || incoming.id;
  const normalizeDriver = driver => ({
    ...driver,
    ...incoming,
    id: incomingUserId || driver.id,
    driver_profile_id: incomingProfileId || driver.driver_profile_id,
  });

  const exists = drivers.some(driver => (
    driver.id === incomingUserId ||
    driver.user_id === incomingUserId ||
    driver.driver_profile_id === incomingProfileId
  ));

  if (!exists) return drivers;

  return drivers.map(driver => (
    driver.id === incomingUserId ||
    driver.user_id === incomingUserId ||
    driver.driver_profile_id === incomingProfileId
      ? normalizeDriver(driver)
      : driver
  ));
}

function getHighestSeverity(alerts = []) {
  if (alerts.some(alert => alert.severity === 'high')) return 'high';
  if (alerts.some(alert => alert.severity === 'medium')) return 'medium';
  if (alerts.some(alert => alert.severity === 'low')) return 'low';
  return null;
}

function statusRank(status) {
  const ranks = {
    pending: 1,
    encamino: 2,
    entregado: 5,
    rechazado: 6,
    cancelado: 7,
  };
  return ranks[status] || 9;
}

function severityRank(severity) {
  const ranks = {
    high: 0,
    medium: 1,
    low: 2,
  };
  return ranks[severity] ?? 9;
}

export function useControlTower(userId) {
  const [user, setUser] = useState({ addresses: [] });
  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selection is intentionally separate from modals. The map keeps operational context visible.
  const [selectedDelivery, setSelectedDelivery] = useState(null); // { type: 'regular', data }

  // Sidebar
  const [searchQuery, setSearchQuery] = useState('');
  const [showList, setShowList] = useState(true);
  const [listFilter, setListFilter] = useState('all'); // all | alerts | encamino | pending

  // Fetch user delivery data for Torre de Control.
  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/user/${userId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUser(res.data || { addresses: [] });
      setDeliveryPoints(res.data?.delivery_points || []);
      setOrders(res.data?.order_points || []);
    } catch (err) {
      console.error('Error fetching tower data:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    fetchData();
    const id = setInterval(fetchData, POLL_MS);
    return () => clearInterval(id);
  }, [fetchData, userId]);

  // Fetch active alerts and subscribe to realtime alert events.
  useEffect(() => {
    if (!userId) return undefined;

    let cancelled = false;
    let inFlight = false;

    const fetchAlerts = async () => {
      if (inFlight) return;
      try {
        inFlight = true;
        const response = await axios.get(`${BASE_URL}/api/delivery-alerts/open`, authConfig());
        if (!cancelled) {
          setAlerts((response.data?.alerts || []).filter(isActiveAlert));
        }
      } catch (err) {
        console.error('Error fetching delivery alerts for control tower:', err);
      } finally {
        inFlight = false;
      }
    };

    const joinPayload = { role: 'customer', user_id: userId };
    const joinRooms = () => socket.emit('join_delivery_alerts', joinPayload);
    const handleAlertCreated = alert => setAlerts(prev => upsertAlert(prev, alert));
    const handleAlertUpdated = alert => setAlerts(prev => upsertAlert(prev, alert));

    fetchAlerts();
    joinRooms();
    const pollId = setInterval(fetchAlerts, ALERTS_POLL_MS);

    socket.on('connect', joinRooms);
    socket.on('delivery_alert_created', handleAlertCreated);
    socket.on('delivery_alert_updated', handleAlertUpdated);

    return () => {
      cancelled = true;
      clearInterval(pollId);
      socket.off('connect', joinRooms);
      socket.off('delivery_alert_created', handleAlertCreated);
      socket.off('delivery_alert_updated', handleAlertUpdated);
      socket.emit('leave_delivery_alerts', joinPayload);
    };
  }, [userId]);

  // Fetch drivers from regular delivery/order assignments only.
  useEffect(() => {
    if (!userId) {
      setDrivers([]);
      return undefined;
    }

    const ids = [
      ...new Set([
        ...deliveryPoints.map(d => d.driver_id).filter(Boolean),
        ...orders.map(o => o.driver_id).filter(Boolean),
      ]),
    ];
    if (!ids.length) {
      setDrivers([]);
      return undefined;
    }

    const fetchDrivers = () => axios.post(
      `${BASE_URL}/api/user/${userId}/control-tower-drivers`,
      { driver_ids: ids },
      authConfig()
    )
      .then(res => setDrivers(res.data.drivers || []))
      .catch(err => console.error('Error fetching drivers:', err));

    const handleDriverLocationUpdated = payload => {
      setDrivers(prev => upsertDriver(prev, {
        id: payload.user_id || payload.id,
        user_id: payload.user_id || payload.id,
        driver_profile_id: payload.driver_profile_id || payload.id,
        name: payload.name,
        username: payload.username || payload.name,
        email: payload.email,
        latitude: payload.latitude,
        longitude: payload.longitude,
        final_latitude: payload.final_latitude ?? payload.home_latitude,
        final_longitude: payload.final_longitude ?? payload.home_longitude,
        last_location_update: payload.last_location_update,
        has_live_location: payload.latitude != null && payload.longitude != null,
      }));
    };

    fetchDrivers();
    const id = setInterval(fetchDrivers, POLL_MS);
    socket.on('driver_location_updated', handleDriverLocationUpdated);

    return () => {
      clearInterval(id);
      socket.off('driver_location_updated', handleDriverLocationUpdated);
    };
  }, [deliveryPoints, orders, userId]);

  const alertsByDeliveryId = useMemo(() => {
    return alerts.reduce((acc, alert) => {
      const deliveryId = alert.delivery_point_id || alert.delivery?.id;
      if (!deliveryId || !isActiveAlert(alert)) return acc;
      if (!acc[deliveryId]) acc[deliveryId] = [];
      acc[deliveryId].push(alert);
      return acc;
    }, {});
  }, [alerts]);

  const alertSummary = useMemo(() => {
    const active = alerts.filter(isActiveAlert);
    return {
      total: active.length,
      high: active.filter(alert => alert.severity === 'high').length,
      medium: active.filter(alert => alert.severity === 'medium').length,
      low: active.filter(alert => alert.severity === 'low').length,
      affectedDeliveries: Object.keys(alertsByDeliveryId).length,
    };
  }, [alerts, alertsByDeliveryId]);

  const filteredRegular = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return deliveryPoints
      .filter(d => {
        const deliveryAlerts = alertsByDeliveryId[d.id] || [];
        if (listFilter === 'alerts' && deliveryAlerts.length === 0) return false;
        if (listFilter === 'encamino' && d.status !== 'encamino') return false;
        if (listFilter === 'pending' && d.status !== 'pending') return false;

        if (!q) return true;
        return (
          d.recipient_name?.toLowerCase().includes(q) ||
          d.recipient_phone?.toLowerCase().includes(q) ||
          d.address?.toLowerCase().includes(q) ||
          d.district?.toLowerCase().includes(q) ||
          String(d.id).includes(q) ||
          deliveryAlerts.some(alert => (
            alert.alert_type?.toLowerCase().includes(q) ||
            alert.message?.toLowerCase().includes(q)
          ))
        );
      })
      .sort((a, b) => {
        const aAlerts = alertsByDeliveryId[a.id] || [];
        const bAlerts = alertsByDeliveryId[b.id] || [];
        const alertDelta = Number(bAlerts.length > 0) - Number(aAlerts.length > 0);
        if (alertDelta) return alertDelta;

        const severityDelta = severityRank(getHighestSeverity(aAlerts)) - severityRank(getHighestSeverity(bAlerts));
        if (severityDelta) return severityDelta;

        return statusRank(a.status) - statusRank(b.status);
      });
  }, [alertsByDeliveryId, deliveryPoints, listFilter, searchQuery]);

  return {
    user,
    deliveryPoints,
    orders,
    drivers,
    alerts,
    alertsByDeliveryId,
    alertSummary,
    loading,
    selectedDelivery,
    setSelectedDelivery,
    searchQuery,
    setSearchQuery,
    showList,
    setShowList,
    listFilter,
    setListFilter,
    filteredRegular,
  };
}
